import type { UserRole } from "@/lib/api/types";

/**
 * Afya24 admin portal is single-tier: a signed-in ADMIN can do
 * everything. `ProtectedRoute` still gates via `can()`, but every
 * capability resolves to "is this an admin?" so there's no per-feature
 * matrix to maintain. The backend remains the source of truth and
 * re-checks every mutation.
 */
export type Capability =
  | "users.view"
  | "roles.view"
  | "doctors.view"
  | "posts.view"
  | "products.view"
  | "transactions.view"
  | "revenue.view"
  | "withdrawals.view"
  | "orders.view"
  | "appointments.view"
  | "catalog.view"
  | "settings.view"
  | "notifications.view"
  | "audit.view"
  | "plans.view"
  | "profile.view";

/** True when the session role is ADMIN. */
export function isAdmin(role: UserRole | string | null | undefined): boolean {
  return role === "ADMIN";
}

/** Capability check — admins hold every capability. */
export function can(
  role: UserRole | string | null | undefined,
  _cap: Capability,
): boolean {
  return isAdmin(role);
}

/** Roles an admin can assign to a user. */
export const ASSIGNABLE_ROLES: UserRole[] = ["CUSTOMER", "DOCTOR", "ADMIN"];

export const ROLE_LABELS: Record<string, string> = {
  CUSTOMER: "Customer",
  DOCTOR: "Doctor",
  ADMIN: "Admin",
};
